import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Copy, Download, Loader2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/lib/utils';
import { getImportableQuotes, importQuotesToAccount } from '../api';

interface ImportQuotesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  accountName: string;
}

const STATUS_VARIANT: Record<string, 'secondary' | 'warning' | 'success' | 'outline' | 'destructive'> = {
  preparation: 'secondary',
  sent: 'warning',
  'waiting-po': 'warning',
  approved: 'success',
  inprogress: 'warning',
  'invoice-ready': 'success',
  'partially-invoiced': 'warning',
  invoiced: 'success',
  cancelled: 'outline',
  rejected: 'destructive',
  expired: 'outline',
};

type SortKey = 'quoteNumber' | 'status' | 'total' | 'createdDate';
type SortDir = 1 | -1;
const PAGE_SIZE = 50;

export default function ImportQuotesModal({
  open,
  onOpenChange,
  accountId,
  accountName,
}: ImportQuotesModalProps) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(1);
  const [page, setPage] = useState(1);

  const { data: allQuotes, isLoading } = useQuery({
    queryKey: ['importable-quotes', accountId],
    queryFn: () => getImportableQuotes(accountId),
    enabled: open,
  });

  const importable = useMemo(() => {
    if (!allQuotes) return [];
    return allQuotes.filter((q) => !q.alreadyImported);
  }, [allQuotes]);

  const filtered = useMemo(() => {
    let result = importable;
    if (statusFilter !== 'all') {
      result = result.filter((q) => q.status === statusFilter);
    }
    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(
        (q) =>
          (q.quoteNumber || '').toLowerCase().includes(lower) ||
          (q.projectName || '').toLowerCase().includes(lower) ||
          (q.name || '').toLowerCase().includes(lower),
      );
    }
    return result;
  }, [importable, search, statusFilter]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortKey(key); setSortDir(1); }
    setPage(1);
  };

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'quoteNumber': cmp = (a.quoteNumber || '').localeCompare(b.quoteNumber || ''); break;
        case 'status': cmp = (a.status || '').localeCompare(b.status || ''); break;
        case 'total': cmp = parseFloat(a.total) - parseFloat(b.total); break;
        case 'createdDate': cmp = (a.createdDate || '').localeCompare(b.createdDate || ''); break;
      }
      return cmp * sortDir;
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const importMutation = useMutation({
    mutationFn: (mongoQuoteIds: string[]) =>
      importQuotesToAccount(accountId, mongoQuoteIds),
    onSuccess: (result) => {
      const total = result.imported + result.reassigned;
      toast.success(`Imported ${total} quote${total !== 1 ? 's' : ''}`);
      if (result.errors.length > 0) {
        toast.warning(`${result.errors.length} quote(s) had errors`, {
          description: result.errors.slice(0, 3).join('\n'),
        });
      }
      queryClient.invalidateQueries({ queryKey: ['account-quotes', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
      queryClient.invalidateQueries({ queryKey: ['importable-quotes', accountId] });
      setSelected(new Set());
      onOpenChange(false);
    },
    onError: () => {
      toast.error('Failed to import quotes');
    },
  });

  const handleImport = () => {
    if (selected.size === 0) return;
    importMutation.mutate(Array.from(selected));
  };

  const toggleAll = () => {
    setSelected(selected.size === sorted.length ? new Set() : new Set(sorted.map((q) => q._id)));
  };

  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ArrowUpDown className="ml-1 inline h-3.5 w-3.5 text-muted-foreground/50" />;
    return sortDir === 1 ? <ArrowUp className="ml-1 inline h-3.5 w-3.5" /> : <ArrowDown className="ml-1 inline h-3.5 w-3.5" />;
  };

  const resetState = () => { setSelected(new Set()); setSearch(''); setStatusFilter('all'); setSortKey(null); setSortDir(1); setPage(1); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetState(); onOpenChange(v); }}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Import Quotes</DialogTitle>
          <DialogDescription>
            Select quotes from the legacy organisation to import into{' '}
            <span className="font-medium text-foreground">{accountName}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 px-1">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input placeholder="Search by quote number or name..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="h-8" />
          {search && <button onClick={() => { setSearch(''); setPage(1); }} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>}
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="h-8 w-[150px] shrink-0">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.keys(STATUS_VARIANT).map((s) => (
                <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 overflow-y-auto border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox checked={sorted.length > 0 && selected.size === sorted.length} onCheckedChange={toggleAll} disabled={sorted.length === 0} />
                </TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('quoteNumber')}>Quote #<SortIcon col="quoteNumber" /></TableHead>
                <TableHead>Name / Project</TableHead>
                <TableHead>Legacy Org ID</TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('status')}>Status<SortIcon col="status" /></TableHead>
                <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort('total')}>Total<SortIcon col="total" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('createdDate')}>Created<SortIcon col="createdDate" /></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 8 }).map((__, j) => <TableCell key={j}><Skeleton className="h-4 w-16" /></TableCell>)}</TableRow>
              ))}
              {!isLoading && sorted.length === 0 && (
                <TableRow><TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                  {importable.length === 0 ? 'All quotes from this organisation are already imported' : 'No matching quotes'}
                </TableCell></TableRow>
              )}
              {!isLoading && paged.map((q) => (
                <TableRow key={q._id} className="cursor-pointer hover:bg-muted/50" onClick={() => toggleOne(q._id)}>
                  <TableCell><Checkbox checked={selected.has(q._id)} onCheckedChange={() => toggleOne(q._id)} /></TableCell>
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-1">
                      {q.quoteNumber || '—'}
                      {q.quoteNumber && (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(q.quoteNumber!); toast.success('Copied quote number'); }}
                          className="text-muted-foreground/50 hover:text-foreground transition-colors"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate">{q.projectName || q.name || '—'}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{q.organisationId || '—'}</TableCell>
                  <TableCell><Badge variant={STATUS_VARIANT[q.status] || 'secondary'}>{q.status}</Badge></TableCell>
                  <TableCell className="text-right tabular-nums">{formatCurrency(parseFloat(q.total), q.currency)}</TableCell>
                  <TableCell>{q.createdDate ? formatDate(q.createdDate) : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <p className="text-sm text-muted-foreground">{selected.size} of {importable.length} selected</p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setPage((p) => p - 1)} disabled={safePage <= 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground px-2">{safePage} / {totalPages}</span>
              <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setPage((p) => p + 1)} disabled={safePage >= totalPages}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
          <Button onClick={handleImport} disabled={selected.size === 0 || importMutation.isPending}>
            {importMutation.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
            {importMutation.isPending ? 'Importing...' : `Import ${selected.size} Quote${selected.size !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
